import { useRef, useState } from 'react';
import { Stack, Button, Text, Image, Group, Alert, Box } from '@mantine/core';
import { UI } from '../../theme/kioskTheme';

const ANCHO_MAX = 1000;
const CALIDAD = 0.82;

/**
 * Reduce la foto antes de subirla. Una tablet saca 3–5 MB por foto y el wifi
 * de un predio con mil personas no los sube (§6 de la ruleta). A 1000 px de
 * ancho quedan unos 150 KB y alcanza de sobra para la pantalla de encuentro
 * y para el muro.
 */
function achicar(file) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const escala = Math.min(1, ANCHO_MAX / img.width);
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * escala);
      c.height = Math.round(img.height * escala);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(b => (b ? resolve(b) : reject(new Error('No se pudo procesar la foto'))),
        'image/jpeg', CALIDAD);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la foto')); };
    img.src = url;
  });
}

/**
 * Captura con la cámara nativa del dispositivo.
 *
 * Se usa `input capture` y no getUserMedia a propósito: son dos toques en vez
 * de pedir permisos, montar un video y dibujar en un canvas, y §7 pide que
 * toda la entrega lleve menos de 15 segundos.
 */
export function CapturaFoto({ label = 'SACAR LA FOTO', onSubir, subiendo, urlActual }) {
  const input = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  async function elegida(e) {
    const file = e.target.files?.[0];
    e.target.value = '';           // permite volver a elegir la misma foto
    if (!file) return;
    setError(null);
    try {
      const blob = await achicar(file);
      setPreview(URL.createObjectURL(blob));
      await onSubir(blob);
    } catch (err) {
      setError(err.message || 'No pudimos procesar la foto.');
      setPreview(null);
    }
  }

  const mostrada = preview || urlActual;

  return (
    <Stack gap="sm">
      <input
        ref={input} type="file" accept="image/*" capture="environment"
        onChange={elegida} style={{ display: 'none' }}
      />

      {mostrada && (
        <Box style={{ width: 200, height: 200, borderRadius: 12, overflow: 'hidden',
                      border: `2px solid ${UI.borde}` }}>
          <Image src={mostrada} alt="Foto" h={200} w={200} fit="cover" />
        </Box>
      )}

      {error && <Alert color="red" radius="md">{error}</Alert>}

      <Group>
        <Button h={64} w={mostrada ? 220 : 300}
                variant={mostrada ? 'outline' : 'filled'}
                loading={subiendo}
                onClick={() => input.current?.click()}>
          {mostrada ? 'SACAR OTRA' : label}
        </Button>
      </Group>

      {mostrada && !subiendo && (
        <Text size="sm" c="forvet.6">Foto guardada</Text>
      )}
    </Stack>
  );
}
